import { LIBRARY_PATH } from '@/constants/appRoutes';
import {
  ArrowLeftRight,
  BadgeCheck,
  BookOpen,
  Building2,
  CircleDot,
  ClipboardCheck,
  ClipboardList,
  Code2,
  DollarSign,
  FileSearch,
  FileText,
  FolderOpen,
  Gavel,
  Landmark,
  LayoutGrid,
  Lock,
  Mail,
  Map,
  MapPin,
  Monitor,
  PieChart,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react';

export const LIBRARY_ROOT_LABEL = 'RCM Library';
export const GOVERNMENT_CATEGORY_SLUG = 'government-regulatory';

export type TopicApiValue =
  | 'government_regulatory'
  | 'coding_classification'
  | 'payer_policies'
  | 'denials_appeals';

export interface LibraryNavItem {
  label: string;
  description: string;
  icon: LucideIcon;
  href: string;
}

export interface LibraryBody {
  slug: string;
  label: string;
  subtitle: string;
  icon: LucideIcon;
  /** When set, lists documents from this ontology subject root code. Otherwise placeholder. */
  topicApiValue?: TopicApiValue;
  tabs: string[];
}

export interface LibraryBucket {
  slug: string;
  label: string;
  description: string;
  icon: LucideIcon;
  topics: string[];
  tabs: string[];
  topicApiValue?: TopicApiValue;
}

export interface LibraryCategory {
  slug: string;
  label: string;
  description: string;
  icon: LucideIcon;
  items: LibraryNavItem[];
  comingSoon: boolean;
  bodies?: LibraryBody[];
  buckets?: LibraryBucket[];
}

export function libraryCategoryPath(slug: string): string {
  return `${LIBRARY_PATH}/${slug}`;
}

export function libraryBodyPath(categorySlug: string, bodySlug: string): string {
  return `${LIBRARY_PATH}/${categorySlug}/bodies/${bodySlug}`;
}

export function libraryBucketPath(categorySlug: string, bucketSlug: string): string {
  return `${LIBRARY_PATH}/${categorySlug}/buckets/${bucketSlug}`;
}

export function libraryComingSoonPath(categorySlug: string, itemLabel?: string): string {
  const base = `${LIBRARY_PATH}/${categorySlug}/coming-soon`;
  if (!itemLabel) return base;
  return `${base}?item=${encodeURIComponent(itemLabel)}`;
}

export const LIBRARY_BODIES: LibraryBody[] = [
  {
    slug: 'cms',
    label: 'CMS',
    subtitle: 'Centers for Medicare & Medicaid Services',
    icon: FileText,
    topicApiValue: 'government_regulatory',
    tabs: ['All', 'Final Rules', 'Proposed Rules', 'Transmittals', 'MLN', 'Manuals', 'Guidance'],
  },
  {
    slug: 'macs',
    label: 'MACs',
    subtitle: 'Medicare Administrative Contractors',
    icon: Users,
    tabs: ['All', 'LCDs', 'Billing Articles', 'Guidance'],
  },
  {
    slug: 'oig',
    label: 'OIG',
    subtitle: 'Office of Inspector General',
    icon: Shield,
    tabs: ['All', 'Audit Reports', 'Fraud Alerts', 'Guidance'],
  },
  {
    slug: 'state-medicaid',
    label: 'State Medicaid',
    subtitle: 'State Medicaid agencies',
    icon: Map,
    tabs: ['All', 'State Plans', 'Bulletins', 'Guidance'],
  },
  {
    slug: 'dmhc-ca',
    label: 'DMHC (CA)',
    subtitle: 'California Department of Managed Health Care',
    icon: Building2,
    tabs: ['All', 'Regulations', 'Guidance'],
  },
  {
    slug: 'all-sources',
    label: 'All Sources',
    subtitle: 'All government and regulatory sources',
    icon: FolderOpen,
    topicApiValue: 'government_regulatory',
    tabs: ['All', 'Final Rules', 'Proposed Rules', 'Transmittals', 'MLN', 'Manuals', 'Guidance'],
  },
];

export const LIBRARY_BUCKETS: LibraryBucket[] = [
  {
    slug: 'front-end',
    label: 'Front End',
    description: 'Registration, eligibility, benefits, prior authorization, and financial clearance.',
    icon: Users,
    topics: [
      'Registration',
      'Eligibility',
      'Benefits',
      'Prior Authorization',
      'Referrals',
      'Estimates',
      'Financial Clearance',
    ],
    tabs: ['All Resources', 'Policies & Manuals', 'Guidance & FAQs', 'Tools & Checklists', 'Training'],
  },
  {
    slug: 'mid-cycle',
    label: 'Mid-Cycle',
    description: 'Documentation, coding, CDI, charge capture, and claim edits.',
    icon: Settings,
    topics: [
      'Documentation',
      'Coding',
      'CDI',
      'Charge Capture',
      'Charge Master',
      'Modifiers',
      'Claim Edits',
    ],
    tabs: ['All Resources', 'Policies & Manuals', 'Coding Resources', 'Tools & Checklists', 'Training'],
    topicApiValue: 'coding_classification',
  },
  {
    slug: 'back-end',
    label: 'Back End',
    description: 'Claim submission, denials, appeals, A/R, and payments.',
    icon: DollarSign,
    topics: [
      'Claim Submission',
      'Denials',
      'Appeals',
      'A/R',
      'Payer Follow-Up',
      'Payments',
      'Refunds',
      'Credit Balances',
    ],
    tabs: ['All Resources', 'Policies & Manuals', 'Denials & Appeals', 'Tools & Checklists', 'Training'],
    topicApiValue: 'denials_appeals',
  },
  {
    slug: 'regulatory-compliance',
    label: 'Regulatory & Compliance',
    description: 'Compliance programs, audits, fraud and abuse, and privacy (HIPAA).',
    icon: ShieldCheck,
    topics: [
      'Compliance',
      'Audits',
      'Fraud & Abuse',
      'Overpayments',
      'Payer Regulations',
      'Privacy (HIPAA)',
    ],
    tabs: ['All Resources', 'Compliance', 'Audits & Monitoring', 'Privacy (HIPAA)', 'Training'],
    topicApiValue: 'government_regulatory',
  },
  {
    slug: 'payer-policies',
    label: 'Payer Policies',
    description: 'Coverage policies, medical necessity, LCDs/NCDs, and billing articles.',
    icon: Landmark,
    topics: [
      'Coverage Policies',
      'Medical Necessity',
      'LCDs/NCDs',
      'Billing Articles',
      'Policy Updates',
    ],
    tabs: ['All Resources', 'Coverage Policies', 'LCDs / NCDs', 'Billing Articles', 'Payer Updates'],
    topicApiValue: 'payer_policies',
  },
  {
    slug: 'coding-billing',
    label: 'Coding & Billing',
    description: 'CPT/HCPCS, ICD-10, modifiers, NCCI edits, and billing guidelines.',
    icon: Code2,
    topics: [
      'CPT/HCPCS',
      'ICD-10',
      'Modifiers',
      'NCCI Edits',
      'Billing Guidelines',
      'Reimbursement Policies',
    ],
    tabs: ['All Resources', 'Coding Systems', 'Billing Guidelines', 'Edits & Modifiers', 'Reimbursement'],
    topicApiValue: 'coding_classification',
  },
  {
    slug: 'payment-reimbursement',
    label: 'Payment & Reimbursement',
    description: 'Fee schedules, payment rules, methodologies, and underpayments.',
    icon: PieChart,
    topics: [
      'Fee Schedules',
      'Payment Rules',
      'Methodologies',
      'Value-Based Programs',
      'Underpayments',
    ],
    tabs: ['All Resources', 'Fee Schedules', 'Payment Rules', 'Methodologies', 'Underpayments'],
  },
  {
    slug: 'patient-financial-services',
    label: 'Patient Financial Services',
    description: 'Patient billing, collections, payment plans, and price transparency.',
    icon: UserRound,
    topics: [
      'Billing Requirements',
      'Patient Collections',
      'Payment Plans',
      'No Surprises Act',
      'Price Transparency',
    ],
    tabs: ['All Resources', 'Billing Requirements', 'Collections', 'Patient Programs', 'Transparency'],
  },
];

export const LIBRARY_CATEGORIES: LibraryCategory[] = [
  {
    slug: GOVERNMENT_CATEGORY_SLUG,
    label: 'Government & Regulatory',
    description: 'Stay informed with official rules, regulations, and compliance guidelines.',
    icon: Landmark,
    items: [
      {
        label: 'CMS Rules & Regulations',
        description: 'Medicare rules, final rules, and policy updates',
        icon: Building2,
        href: libraryBodyPath(GOVERNMENT_CATEGORY_SLUG, 'cms'),
      },
      {
        label: 'Federal Register',
        description: 'Federal notices, proposed rules, and published regulations',
        icon: FileText,
        href: libraryComingSoonPath(GOVERNMENT_CATEGORY_SLUG, 'Federal Register'),
      },
      {
        label: 'State Regulations',
        description: 'State Medicaid rules, bulletins, and plan updates',
        icon: MapPin,
        href: libraryComingSoonPath(GOVERNMENT_CATEGORY_SLUG, 'State Regulations'),
      },
      {
        label: 'HIPAA & Privacy',
        description: 'Privacy, security, and patient data requirements',
        icon: Lock,
        href: libraryComingSoonPath(GOVERNMENT_CATEGORY_SLUG, 'HIPAA & Privacy'),
      },
      {
        label: 'OIG Guidance',
        description: 'Audits, fraud alerts, and compliance guidance',
        icon: Shield,
        href: libraryComingSoonPath(GOVERNMENT_CATEGORY_SLUG, 'OIG Guidance'),
      },
      {
        label: 'Legislation & Law',
        description: 'Statutes and legislative updates affecting RCM',
        icon: Gavel,
        href: libraryComingSoonPath(GOVERNMENT_CATEGORY_SLUG, 'Legislation & Law'),
      },
    ],
    comingSoon: false,
    bodies: LIBRARY_BODIES,
    buckets: LIBRARY_BUCKETS,
  },
  {
    slug: 'coding-classification',
    label: 'Coding & Classification',
    description: 'Find coding standards, guidelines, and classification resources.',
    icon: BookOpen,
    items: [
      {
        label: 'ICD-10-CM',
        description: 'Diagnosis coding guidelines and updates',
        icon: BookOpen,
        href: libraryComingSoonPath('coding-classification', 'ICD-10-CM'),
      },
      {
        label: 'CPT® Codes',
        description: 'Procedure coding standards and guidance',
        icon: CircleDot,
        href: libraryComingSoonPath('coding-classification', 'CPT® Codes'),
      },
      {
        label: 'HCPCS Level II',
        description: 'Supplies, drugs, and services codes',
        icon: FileText,
        href: libraryComingSoonPath('coding-classification', 'HCPCS Level II'),
      },
      {
        label: 'Modifiers',
        description: 'Billing modifiers and usage rules',
        icon: Code2,
        href: libraryComingSoonPath('coding-classification', 'Modifiers'),
      },
      {
        label: 'NCCI Edits',
        description: 'Procedure-to-procedure edit guidance',
        icon: LayoutGrid,
        href: libraryComingSoonPath('coding-classification', 'NCCI Edits'),
      },
      {
        label: 'Code Sets & Crosswalks',
        description: 'Code mappings and translation resources',
        icon: ArrowLeftRight,
        href: libraryComingSoonPath('coding-classification', 'Code Sets & Crosswalks'),
      },
    ],
    comingSoon: true,
  },
  {
    slug: 'payer-resources',
    label: 'Payer Resources',
    description: 'Access payer policies, manuals, and provider resources.',
    icon: Building2,
    items: [
      {
        label: 'Medicare',
        description: 'Medicare manuals, policies, and billing rules',
        icon: Landmark,
        href: libraryComingSoonPath('payer-resources', 'Medicare'),
      },
      {
        label: 'Medicaid',
        description: 'Medicaid coverage and billing resources',
        icon: Shield,
        href: libraryComingSoonPath('payer-resources', 'Medicaid'),
      },
      {
        label: 'Commercial Payers',
        description: 'Commercial medical and payment policies',
        icon: FileText,
        href: libraryComingSoonPath('payer-resources', 'Commercial Payers'),
      },
      {
        label: 'Prior Authorization',
        description: 'Auth requirements and payer processes',
        icon: BadgeCheck,
        href: libraryComingSoonPath('payer-resources', 'Prior Authorization'),
      },
      {
        label: 'Medical Policies',
        description: 'Coverage and medical necessity policies',
        icon: FileText,
        href: libraryComingSoonPath('payer-resources', 'Medical Policies'),
      },
      {
        label: 'Provider Portals',
        description: 'Payer portal access and job aids',
        icon: Monitor,
        href: libraryComingSoonPath('payer-resources', 'Provider Portals'),
      },
    ],
    comingSoon: true,
  },
  {
    slug: 'denials-appeals-compliance',
    label: 'Denials, Appeals & Compliance',
    description: 'Tools and guidance to manage denials, appeals, and ensure compliance.',
    icon: ClipboardList,
    items: [
      {
        label: 'Denial Management',
        description: 'Root-cause analysis and prevention resources',
        icon: Mail,
        href: libraryComingSoonPath('denials-appeals-compliance', 'Denial Management'),
      },
      {
        label: 'Appeal Process',
        description: 'Appeal timelines, letters, and workflows',
        icon: FileText,
        href: libraryComingSoonPath('denials-appeals-compliance', 'Appeal Process'),
      },
      {
        label: 'Compliance Programs',
        description: 'Program structure and requirements',
        icon: ShieldCheck,
        href: libraryComingSoonPath('denials-appeals-compliance', 'Compliance Programs'),
      },
      {
        label: 'Audit & Risk',
        description: 'Audit preparation and risk monitoring',
        icon: FileSearch,
        href: libraryComingSoonPath('denials-appeals-compliance', 'Audit & Risk'),
      },
      {
        label: 'Fraud & Abuse',
        description: 'Fraud, waste, and abuse guidance',
        icon: ShieldAlert,
        href: libraryComingSoonPath('denials-appeals-compliance', 'Fraud & Abuse'),
      },
      {
        label: 'Checklists & Templates',
        description: 'Practical tools for daily RCM work',
        icon: ClipboardCheck,
        href: libraryComingSoonPath('denials-appeals-compliance', 'Checklists & Templates'),
      },
    ],
    comingSoon: true,
  },
];

export const LIBRARY_TOP_TOPICS: { label: string; bucketSlug: string; share: number }[] = [
  { label: 'Prior Authorization', bucketSlug: 'front-end', share: 100 },
  { label: 'Medical Necessity', bucketSlug: 'payer-policies', share: 86 },
  { label: 'Denials & Appeals', bucketSlug: 'back-end', share: 74 },
  { label: 'Documentation Requirements', bucketSlug: 'mid-cycle', share: 61 },
  { label: 'Coding Guidelines', bucketSlug: 'coding-billing', share: 52 },
  { label: 'Reimbursement Policies', bucketSlug: 'payment-reimbursement', share: 41 },
  { label: 'Telehealth', bucketSlug: 'payer-policies', share: 28 },
];

export type LibraryBreadcrumbSegment = { label: string; path: string };

export function getLibraryCategory(slug: string | undefined | null): LibraryCategory | undefined {
  if (!slug) return undefined;
  return LIBRARY_CATEGORIES.find((category) => category.slug === slug);
}

export function isLibraryCategorySlug(slug: string | undefined | null): boolean {
  return getLibraryCategory(slug) != null;
}

export function getLibraryBody(slug: string | undefined | null): LibraryBody | undefined {
  if (!slug) return undefined;
  return LIBRARY_BODIES.find((body) => body.slug === slug);
}

export function getLibraryBucket(slug: string | undefined | null): LibraryBucket | undefined {
  if (!slug) return undefined;
  return LIBRARY_BUCKETS.find((bucket) => bucket.slug === slug);
}

export function libraryBreadcrumbSegments(pathname: string): LibraryBreadcrumbSegment[] | null {
  if (pathname !== LIBRARY_PATH && !pathname.startsWith(`${LIBRARY_PATH}/`)) return null;

  const parts = pathname.split('/').filter(Boolean);
  const root: LibraryBreadcrumbSegment = { label: LIBRARY_ROOT_LABEL, path: LIBRARY_PATH };

  if (parts.length === 1) return [root];
  if (parts[1] === 'search' || parts[1] === 'documents' || parts[1] === 'questions') return null;

  const category = getLibraryCategory(parts[1]);
  if (!category) return null;

  const categorySeg: LibraryBreadcrumbSegment = {
    label: category.label,
    path: libraryCategoryPath(category.slug),
  };

  if (parts.length === 2) return [root, categorySeg];

  if (parts[2] === 'coming-soon') {
    return [root, categorySeg, { label: 'Coming soon', path: `${categorySeg.path}/coming-soon` }];
  }

  if (parts[2] === 'bodies' && parts[3]) {
    const body = getLibraryBody(parts[3]);
    if (!body) return [root, categorySeg];
    return [
      root,
      categorySeg,
      { label: body.label, path: libraryBodyPath(category.slug, body.slug) },
    ];
  }

  if (parts[2] === 'buckets' && parts[3]) {
    const bucket = getLibraryBucket(parts[3]);
    if (!bucket) return [root, categorySeg];
    return [
      root,
      categorySeg,
      { label: bucket.label, path: libraryBucketPath(category.slug, bucket.slug) },
    ];
  }

  return [root, categorySeg];
}

export const SOURCE_FILTER_PENDING_MESSAGE =
  'Documents for this source will appear once source tagging is available in the backend.';

export const BUCKET_FILTER_PENDING_MESSAGE =
  'Documents for this bucket will appear once bucket tagging is available in the backend.';
